import baseApi, { GenericResponse } from 'services/api/base';
import getProductFromPath from 'helper/products/similarity/getProductFromPath';

// Tipos de dados
export interface Variable {
  id: string;
  nome: string;
  label: string;
}

export interface ClimateIndex {
  ano: number;
  mes: number;
  'nino3.4': number;
  iod: number;
  tna: number;
  tsa: number;
  tad: number;
  sad: number;
  amo: number;
  sac: number;
  pdo: number;
  aao: number;
}

export interface AnomalyMap {
  nome: string;
  link: string;
  ano: number;
}

export interface EnsembleResult {
  url: string;
  var: string;
  mes: string;
  anos: number[];
}

// Parâmetros de requisição
export interface GetMapsParams {
  variavel: string;
  mes: number;
  anos: number[];
}

export interface GenerateEnsembleParams {
  dict_request: {
    variavel: string;
    mes: number;
    anos: number[];
  };
  email: string;
}

// Transform functions
const transformListVariablesResponse = (
  response: GenericResponse<{ data: Variable[] }>,
): Variable[] => {
  if (response.status === 1 && response.data?.data) {
    return response.data.data;
  }
  return [];
};

const transformClimateIndicesResponse = (
  response: GenericResponse<{ data: ClimateIndex[] }>,
): ClimateIndex[] => {
  if (response.status === 1 && response.data?.data) {
    return response.data.data;
  }
  return [];
};

const transformGetMapsResponse = (
  response: GenericResponse<{ data: AnomalyMap[] }>,
): AnomalyMap[] => {
  if (response.status === 1 && response.data?.data) {
    return response.data.data;
  }
  return [];
};

const transformGenerateEnsembleResponse = (
  response: GenericResponse<{ data: EnsembleResult } | EnsembleResult>,
): EnsembleResult | null => {
  // Tratamento para múltiplos formatos possíveis de resposta
  if (response.status === 1) {
    // Caso 1: response.data.data (estrutura aninhada)
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data;
    }
    // Caso 2: response.data direto (estrutura simples)
    if (response.data && typeof response.data === 'object' && 'url' in response.data) {
      return response.data as EnsembleResult;
    }
  }

  return null;
};

export const similarityApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Listar variáveis
    listSimilarityVariables: build.query<Variable[], void>({
      query: () => {
        const productKey = getProductFromPath();
        return {
          url: `produtos/similaridade/listar-variaveis?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformListVariablesResponse,
    }),

    // Retornar dados de índices climáticos
    getClimateIndices: build.query<ClimateIndex[], void>({
      query: () => {
        const productKey = getProductFromPath();
        return {
          url: `produtos/similaridade/retornar-dados-indices-climaticos?product_key=${productKey}`,
          method: 'GET',
        };
      },
      transformResponse: transformClimateIndicesResponse,
    }),

    // Obter mapas de anos filtrados
    getFilteredYearsMaps: build.mutation<AnomalyMap[], GetMapsParams>({
      query: (params) => {
        const productKey = getProductFromPath();
        return {
          url: `produtos/similaridade/obter-mapas-anos-filtrados?product_key=${productKey}`,
          method: 'POST',
          body: {
            variavel: params.variavel,
            mes: params.mes,
            anos: params.anos,
          },
        };
      },
      transformResponse: transformGetMapsResponse,
    }),

    // Gerar ensemble
    generateEnsemble: build.mutation<EnsembleResult | null, GenerateEnsembleParams>({
      query: (params) => {
        const productKey = getProductFromPath();

        const requestBody = {
          dict_request: {
            variavel: params.dict_request.variavel,
            mes: params.dict_request.mes,
            anos: params.dict_request.anos,
          },
        };

        return {
          url: `produtos/similaridade/gerar-ensemble?product_key=${productKey}`,
          method: 'POST',
          body: requestBody,
        };
      },
      transformResponse: transformGenerateEnsembleResponse,
    }),
  }),
});

export const {
  useListSimilarityVariablesQuery,
  useGetClimateIndicesQuery,
  useGetFilteredYearsMapsMutation,
  useGenerateEnsembleMutation,
} = similarityApi;
